import styled from 'styled-components';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ICONS_ALIASES } from '_constants/icons';
import { ROUTES } from '_constants/routes';

import Button from '_components/atomic/Button';
import Icon from '_components/atomic/Icon';
import { Divider } from '_components/atomic/layouts';
import ServiceInfoSection from '_components/composed/sections/ServiceInfoSection';
import BottomBannerSection from '_components/composed/sections/BottomBannerSection';
import MainBannerSection from '_components/composed/sections/MainBannerSection';

const { publicRuntimeConfig } = getConfig();
const { colors, spaces } = theme;

const StyledServicesPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ServicesPage = () => {
  const router = useRouter();

  return (
    <StyledServicesPage>
      <MainBannerSection
        title="Our Services"
        topText="At Incorpified, we understand that starting and managing a business can be challenging, that's why we offer a wide range of services to make the process as simple and stress-free as possible. From incorporation and ongoing compliance, to trademark registration, bookkeeping, merchant processing and more, our team of experts is here to help you every step of the way. We strive to provide personalized service and guidance to ensure your business's success. Whether you're just starting out or looking to expand, we've got the tools and resources to help you achieve your goals."
        desktopImage={`${config.basePath}/images/services-banner-desktop.png`}
        mobileImage={`${config.basePath}/images/services-banner-mobile.png`}
        buttonsBlock={
          <Button
            suffix={
              <Icon alias={ICONS_ALIASES.arrowRight} color={colors.white} />
            }
            color={colors.indigo[600]}
            onClick={() => {
              router.push(ROUTES.llcIncorporation);
            }}
          >
            Start Your Business
          </Button>
        }
      />
      <ServiceInfoSection
        imgSrc={`${publicRuntimeConfig.basePath}/images/llc-incorporation-service.svg`}
        subtitle=""
        title="LLC Incorporation"
        description={
          <>
            Streamlined incorporation service that takes the hassle out of
            starting a business. Our experts will help you navigate the process,
            from selecting the appropriate business structure to filing all
            necessary documents with the state, we&apos;ve got you covered.
            Trust us to handle the legal side of things, leaving you free to
            focus on building and growing your business.
          </>
        }
        onClick={() => {
          router.push(ROUTES.llcIncorporation);
        }}
      />

      <ServiceInfoSection
        reverse
        imgSrc={`${publicRuntimeConfig.basePath}/images/ein-tax-id-service.svg`}
        color={colors.indigo[25]}
        subtitle=""
        title="EIN/Tax ID"
        description={
          <>
            EIN/Tax ID filing service ensures that your business is fully
            compliant with the IRS. An EIN is required for businesses to pay
            taxes, open a business bank account and hire employees. We can help
            you file the SS-4 and fix any issues that may arise through the
            process.
          </>
        }
        onClick={() => {
          router.push(ROUTES.einTaxId);
        }}
      />

      <ServiceInfoSection
        imgSrc={`${publicRuntimeConfig.basePath}/images/registered-agent-service.svg`}
        subtitle=""
        title="Registered Agent Service"
        description={
          <>
            A Registered Agent service ensures that your business stays in
            compliance with state regulations. A registered agent is responsible
            for receiving and forwarding legal documents on behalf of your
            business. It is an important service to have, as failure to maintain
            a registered agent can lead to penalties and loss of good standing.
            We&apos;ll take care of this critical aspect of your business.
          </>
        }
        onClick={() => {
          router.push(ROUTES.registeredAgent);
        }}
      />

      <ServiceInfoSection
        reverse
        imgSrc={`${publicRuntimeConfig.basePath}/images/trademarketing-service.svg`}
        color={colors.green[25]}
        subtitle=""
        title="Trademarking"
        description={
          <>
            Trademark registration protects your business&apos;s name and logo
            from infringement. A trademark ensures that your business has
            exclusive rights to use that name or logo in commerce. Without a
            trademark, your business name and logo are vulnerable to
            infringement. We&apos;ll help you file for trademark registration
            and protect your business&apos;s unique identity and brand.
          </>
        }
        onClick={() => {
          router.push(ROUTES.trademarking);
        }}
      />

      <ServiceInfoSection
        imgSrc={`${publicRuntimeConfig.basePath}/images/bookkeeping-service.svg`}
        subtitle=""
        title="Bookkeeping"
        description={
          <>
            Bookkeeping service ensures that your business&apos;s financial
            records are accurate and up-to-date. Bookkeeping is important to
            maintain financial stability, compliance with tax laws, and to track
            expenses and revenues. Our bookkeeping service makes it easy for you
            to stay on top of your business&apos;s finances, making it easier to
            make informed decisions.
          </>
        }
        onClick={() => {
          router.push(ROUTES.bookkeeping);
        }}
      />

      <ServiceInfoSection
        reverse
        imgSrc={`${publicRuntimeConfig.basePath}/images/merchant-processing-service.svg`}
        color={colors.indigo[25]}
        subtitle=""
        title="Merchant Processing"
        description={
          <>
            Merchant Processing allows your business to accept payments easily
            and securely. It is an important service for businesses that operate
            online or in-person, as it ensures that customers can make purchases
            quickly and easily. Our merchant processing partners make it easy
            for you to accept a variety of payment methods, including credit and
            debit cards, ensuring that your customers have a seamless purchasing
            experience.
          </>
        }
        onClick={() => {
          router.push(ROUTES.merchantProcessing);
        }}
      />

      <ServiceInfoSection
        imgSrc={`${publicRuntimeConfig.basePath}/images/payroll-service.svg`}
        subtitle=""
        title="Payroll"
        description={
          <>
            Payroll is an important aspect of any business, and managing it
            effectively can be time-consuming and complex. Our payroll service
            offers a solution, simplifying the payroll process and freeing up
            valuable time for business owners and managers. With our
            comprehensive service, tax calculations, direct deposit, and all
            other payroll-related tasks are handled with ease, giving businesses
            the peace of mind they need to focus on growth and success.
          </>
        }
        onClick={() => {
          router.push(ROUTES.payroll);
        }}
      />

      <ServiceInfoSection
        reverse
        imgSrc={`${publicRuntimeConfig.basePath}/images/virtual-business-address-service.svg`}
        color={colors.green[25]}
        subtitle=""
        title="Virtual Business Address"
        description={
          <>
            A Virtual Business Address provides you with a professional mailing
            address, phone and email service and allows you to conduct business
            remotely. It is an important service for businesses that want to
            keep operational costs low while still maintaining a professional
            image. Our virtual business service also allows you to expand your
            reach and conduct business from anywhere, providing a flexible and
            efficient way to manage your business.
          </>
        }
        onClick={() => {
          router.push(ROUTES.virtualAddress);
        }}
      />

      <Divider size={spaces.large24X} desktop />
      {/* <Divider size={spaces.large} mobile /> */}

      <BottomBannerSection
        header={<>Get Your Business Incorporated Today</>}
        footer={
          <>
            <p>Here are some services that business owners use</p>
            <div className="buttonsContainer">
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Start Your LLC
              </Button>
              <Button
                suffix={
                  <Icon
                    alias={ICONS_ALIASES.arrowRight}
                    color={colors.indigo[600]}
                  />
                }
                color={colors.white}
                textColor={colors.indigo[600]}
                hoverColor={colors.white}
                hoverTextColor={colors.indigo[600]}
              >
                Start Your Corporation
              </Button>
            </div>
          </>
        }
        moreSpace
      />
      <Divider size={spaces.large24X} desktop />
      <Divider size={spaces.large} mobile />
    </StyledServicesPage>
  );
};

export default ServicesPage;
